<template>
  <div ref="root" class="mini-plot-preview">
    <div
      v-if="status === 'empty' || status === 'error'"
      class="mini-plot-error"
      :title="status === 'error' ? errorMessage : 'No data'"
    >
      <v-icon v-if="status === 'error'" size="14" class="mini-plot-error-icon">
        mdi-alert-circle-outline
      </v-icon>
      <v-icon v-else size="14" class="mini-plot-error-icon">mdi-chart-scatter-plot</v-icon>
    </div>
    <template v-else>
      <div v-if="status === 'loading'" class="mini-plot-skeleton overlay" aria-hidden="true" />
      <div ref="plotEl" class="mini-plot-mount" :class="{ 'plot-hidden': status === 'loading' }" />
    </template>
  </div>
</template>

<script>
import Plotly from "plotly.js-dist-min";
import api from "@/axiosInterceptor";
import { getTenantPrefix } from "@/utils/tenant";
import { mapGetters } from "vuex";
import {
  transformToMiniPlot,
  buildFilterQueryParams,
} from "@/utils/plotlyMiniTransform";

export default {
  name: "MiniPlotPreview",
  props: {
    /** Same shape as ChartCard: { data: chartMetadata, chartData?: rows } */
    plotConfig: {
      type: Object,
      default: null,
    },
    maxPoints: {
      type: Number,
      default: 220,
    },
    /** IntersectionObserver root margin */
    rootMargin: {
      type: String,
      default: "80px",
    },
  },
  data() {
    return {
      /** loading | ready | empty | error */
      status: "loading",
      errorMessage: "",
      io: null,
      shouldLoad: false,
      plotScheduled: false,
      loadRunning: false,
      cachedMeta: null,
      cachedRows: null,
    };
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    tenantId() {
      return getTenantPrefix();
    },
  },
  watch: {
    plotConfig: {
      deep: true,
      handler() {
        this.resetAndMaybeReload();
      },
    },
    isDarkMode() {
      this.applyThemeToMiniPlot();
    },
  },
  mounted() {
    this.setupObserver();
  },
  beforeUnmount() {
    this.teardownObserver();
    this.destroyPlot();
  },
  methods: {
    resetAndMaybeReload() {
      this.destroyPlot();
      this.cachedMeta = null;
      this.cachedRows = null;
      this.plotScheduled = false;
      this.loadRunning = false;
      this.errorMessage = "";
      if (this.shouldLoad) {
        this.status = "loading";
        this.$nextTick(() => {
          this.runLoadPipeline();
        });
      } else {
        this.status = "loading";
      }
    },
    isElementVisible(el) {
      if (!el) return false;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const vw = window.innerWidth || document.documentElement.clientWidth;
      return r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw;
    },
    setupObserver() {
      if (typeof IntersectionObserver === "undefined") {
        this.shouldLoad = true;
        this.runLoadPipeline();
        return;
      }
      this.io = new IntersectionObserver(
        (entries) => {
          const hit = entries.some((e) => e.isIntersecting);
          if (hit && !this.shouldLoad) {
            this.shouldLoad = true;
            this.$nextTick(() => this.runLoadPipeline());
          }
        },
        { root: null, rootMargin: this.rootMargin, threshold: 0.01 }
      );
      this.io.observe(this.$refs.root);
      this.$nextTick(() => {
        if (!this.shouldLoad && this.isElementVisible(this.$refs.root)) {
          this.shouldLoad = true;
          this.runLoadPipeline();
        }
      });
    },
    teardownObserver() {
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
    },
    destroyPlot() {
      const el = this.$refs.plotEl;
      if (!el) return;
      try {
        Plotly.purge(el);
      } catch {
        /* ignore */
      }
      el.innerHTML = "";
    },
    normalizeRows(samples) {
      if (samples == null) return null;
      if (Array.isArray(samples) && samples.length) return samples;
      if (typeof samples === "object" && samples.rows && Array.isArray(samples.rows)) {
        return samples.rows;
      }
      return null;
    },
    processDecimalRows(rows) {
      return rows.map((row) => {
        const copy = { ...row };
        Object.keys(copy).forEach((key) => {
          if (key + "_decimal" in copy && copy[key + "_decimal"] !== null) {
            const numValue = parseFloat(copy[key]);
            if (!Number.isNaN(numValue)) {
              copy[key] = numValue;
            }
          }
        });
        return copy;
      });
    },
    async ensureMetadata() {
      const cfg = this.plotConfig;
      if (!cfg) return null;
      let meta = cfg.data || cfg;
      if (meta && meta.ID && meta.ChartType) {
        return meta;
      }
      const id = meta?.ID || meta?.id;
      if (!id) return null;
      const response = await api.get(`public/api/charts/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "X-TenantID": this.tenantId,
        },
      });
      if (
        response?.data &&
        (response.data.statusCode === 200 || response.data.statusCode === 201)
      ) {
        return response.data.data?.chart || response.data.data;
      }
      return null;
    },
    async fetchChartRows(chartMeta) {
      if (!chartMeta?.ID) return null;
      const filterParams = buildFilterQueryParams(chartMeta.Filter);
      const queryString = Object.entries(filterParams)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join("&");
      const url = queryString
        ? `public/api/charts/data/${chartMeta.ID}?${queryString}`
        : `public/api/charts/data/${chartMeta.ID}`;
      const response = await api.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          "X-TenantID": this.tenantId,
        },
      });
      if (
        !response?.data ||
        !(response.data.statusCode === 200 || response.data.statusCode === 201)
      ) {
        return null;
      }
      const raw = this.normalizeRows(response.data.data?.samples);
      if (!raw?.length) return null;
      return this.processDecimalRows(raw);
    },
    async runLoadPipeline() {
      if (!this.shouldLoad || !this.plotConfig) {
        this.status = "empty";
        return;
      }
      if (this.loadRunning) {
        return;
      }
      this.loadRunning = true;
      this.status = "loading";
      this.plotScheduled = false;
      this.errorMessage = "";

      try {
        const meta = await this.ensureMetadata();
        if (!meta) {
          this.status = "error";
          this.errorMessage = "No chart";
          return;
        }

        let rows = this.plotConfig.chartData;
        if (!rows || !Array.isArray(rows) || rows.length === 0) {
          rows = await this.fetchChartRows(meta);
        } else {
          rows = this.processDecimalRows(rows);
        }

        if (!rows || !rows.length) {
          this.status = "empty";
          return;
        }

        this.cachedMeta = meta;
        this.cachedRows = rows;

        const transformed = transformToMiniPlot(
          { data: meta, chartData: rows },
          { maxPoints: this.maxPoints, isDark: this.isDarkMode }
        );

        if (transformed.error) {
          this.cachedMeta = null;
          this.cachedRows = null;
          this.status = "error";
          this.errorMessage = transformed.error;
          return;
        }

        this.schedulePlotly(transformed.traces, transformed.layout, transformed.config);
      } catch (e) {
        console.warn("MiniPlotPreview load failed", e);
        this.status = "error";
        this.errorMessage = "Load failed";
      } finally {
        this.loadRunning = false;
      }
    },
    schedulePlotly(traces, layout, config) {
      if (this.plotScheduled) return;
      this.plotScheduled = true;
      requestAnimationFrame(() => {
        this.$nextTick(() => {
          this.renderPlot(traces, layout, config);
        });
      });
    },
    applyThemeToMiniPlot() {
      const el = this.$refs.plotEl;
      if (
        !el ||
        this.status !== "ready" ||
        !this.cachedMeta ||
        !this.cachedRows ||
        !this.cachedRows.length
      ) {
        return;
      }
      const transformed = transformToMiniPlot(
        { data: this.cachedMeta, chartData: this.cachedRows },
        { maxPoints: this.maxPoints, isDark: this.isDarkMode }
      );
      if (transformed.error) return;
      Plotly.react(el, transformed.traces, transformed.layout, transformed.config).catch(() => {
        Plotly.newPlot(el, transformed.traces, transformed.layout, transformed.config).catch(() => {});
      });
    },
    renderPlot(traces, layout, config) {
      const el = this.$refs.plotEl;
      if (!el) {
        this.status = "error";
        this.errorMessage = "No container";
        return;
      }

      const w = el.offsetWidth || el.clientWidth || 120;
      const h = el.offsetHeight || el.clientHeight || 80;
      if (w < 8 || h < 8) {
        this.plotScheduled = false;
        setTimeout(() => this.renderPlot(traces, layout, config), 80);
        return;
      }

      this.destroyPlot();
      el.style.width = `${w}px`;
      el.style.height = `${h}px`;

      Plotly.newPlot(el, traces, layout, config)
        .then(() => {
          this.status = "ready";
          this.errorMessage = "";
        })
        .catch((err) => {
          console.warn("MiniPlotPreview Plotly error", err);
          this.status = "error";
          this.errorMessage = "Plot error";
        });
    },
  },
};
</script>
